import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import {
  fetchSapAuthRequests,
  fetchSapAuthMasterAll,
  deleteDraftSapAuthRequest,
  syncSapAuthRequests,
  ISapAuthorizationRequest,
  ISapMasterOption
} from '../../../core/data/redux/sapAuthorizationSlice';
import {
  getRequestStatusConfig,
  getRequestStatusLabel,
  SapRequestStatus
} from '../../../core/data/constants/requestStatus';

export const getMasterText = (code?: string, list?: ISapMasterOption[]): string => {
  if (!code) return '-';
  const clean = String(code).trim();
  if (Array.isArray(list)) {
    const found = list.find((m) => String(m.Value).trim().toUpperCase() === clean.toUpperCase());
    if (found?.Ddtext) return String(found.Ddtext).trim();
  }
  return code;
};

export const getSapStatusBadge = (statusStr?: string, isDraft?: boolean) => {
  if (isDraft) {
    return <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2 py-1"><i className="ti ti-file-pencil me-1"></i>Draft</span>;
  }
  const s = String(statusStr || '').trim();
  const config = getRequestStatusConfig(s);
  const lower = s.toLowerCase();

  if (config.badgeClass && config.code === s.toUpperCase()) {
    return <span className={`${config.badgeClass} px-2 py-1`}><i className={`${config.icon} me-1`}></i>{config.label}</span>;
  }
  if (lower.includes('approve') || lower === 'a' || lower === 'd') {
    return <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1"><i className="ti ti-circle-check me-1"></i>Approved</span>;
  }
  if (lower.includes('pending') || lower.includes('in-process') || lower === 'p' || lower === 't' || lower === 'v' || lower === 'w') {
    return <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-2 py-1"><i className="ti ti-clock me-1"></i>Pending</span>;
  }
  if (lower.includes('reject') || lower === 'r' || lower === 'k' || lower === 'x') {
    return <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1"><i className="ti ti-circle-x me-1"></i>Rejected</span>;
  }
  return <span className="badge bg-info-subtle text-info border border-info-subtle px-2 py-1"><i className="ti ti-info-circle me-1"></i>{s || 'Submitted'}</span>;
};

const SapAuthorizationList: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const sapState = useSelector((state: RootState) => state.sapAuthorizationState);

  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    query: '',
    requestStatus: '',
    server: '',
    module: '',
    plant: '',
    startDate: '',
    endDate: '',
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deleteItem, setDeleteItem] = useState<ISapAuthorizationRequest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isSyncing, setIsSyncing] = useState(false);

  const handleSyncSapAuth = async () => {
    setIsSyncing(true);
    try {
      const res = await dispatch(syncSapAuthRequests({ type: 'All' }));
      if (res.meta.requestStatus === 'fulfilled') {
        toast.success('SAP Authorization requests synchronized');
        loadRequests();
      } else {
        toast.error((res.payload as string) || 'Failed to sync SAP requests');
      }
    } catch {
      toast.error('An error occurred during SAP sync');
    } finally {
      setIsSyncing(false);
    }
  };

  const loadRequests = () => {
    dispatch(
      fetchSapAuthRequests({
        page,
        limit,
        query: filters.query,
        requestStatus: filters.requestStatus,
        startDate: filters.startDate,
        endDate: filters.endDate,
        orderBy: 'DESC',
        orderColumn: 'id',
      })
    );
  };

  useEffect(() => {
    dispatch(fetchSapAuthMasterAll({}));
  }, [dispatch]);

  useEffect(() => {
    loadRequests();
  }, [dispatch, page, limit, filters.requestStatus, filters.startDate, filters.endDate]);

  const masterData = sapState?.masterData || {};
  const plantsList = masterData.Plants || [];
  const serversList = masterData.SAP_servers || [];
  const modulesList = masterData.SAP_Modules || [];
  const userCatList = masterData.User_Categories || [];
  const businessAreasList = masterData.Business_Areas || [];

  // Parse list data from different possible API structures
  const rawList =
    sapState?.listData?.data?.data ||
    sapState?.listData?.data?.results ||
    sapState?.listData?.data ||
    sapState?.listData?.results ||
    (Array.isArray(sapState?.listData) ? sapState.listData : []);

  const allItems: ISapAuthorizationRequest[] = Array.isArray(rawList) ? rawList : [];

  // Client-side filtering for module/server/plant/search when API does partial filter
  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      if (filters.query) {
        const q = filters.query.toLowerCase();
        const refNo = String(item.ReferenceNumber || item.referenceNumber || '').toLowerCase();
        const empId = String(item.EmpID || item.empId || '').toLowerCase();
        const empName = String(item.EmpName || item.EmployeeName || item.employeeName || '').toLowerCase();
        const remarks = String(item.Remarks || item.remarks || '').toLowerCase();
        const dept = String(item.Department || '').toLowerCase();
        const itemTcodes: any = item.ToTcodes;
        const tcodesArray: any[] = itemTcodes && typeof itemTcodes === 'object' && Array.isArray(itemTcodes.results)
          ? itemTcodes.results
          : Array.isArray(itemTcodes)
            ? itemTcodes
            : [];
        const matchesTcode = tcodesArray.some((t: any) =>
          String(t.TransactionCode || '').toLowerCase().includes(q)
        );
        if (!refNo.includes(q) && !empId.includes(q) && !empName.includes(q) && !remarks.includes(q) && !dept.includes(q) && !matchesTcode) {
          return false;
        }
      }
      if (filters.server && String(item.SAPServer || '').toUpperCase() !== filters.server.toUpperCase()) {
        return false;
      }
      if (filters.module && String(item.SAPModule || '').toUpperCase() !== filters.module.toUpperCase()) {
        return false;
      }
      if (filters.plant && String(item.Plant || '').toUpperCase() !== filters.plant.toUpperCase()) {
        return false;
      }
      return true;
    });
  }, [allItems, filters]);

  // KPI Calculations
  const kpis = useMemo(() => {
    let pending = 0;
    let approved = 0;
    let rejected = 0;
    let drafts = 0;

    allItems.forEach((item) => {
      const isDraft = Boolean(item.draft || item.is_draft);
      const status = String(item.RequestStatus || item.requestStatus || item.Status || item.status || '').toLowerCase();
      if (isDraft) {
        drafts++;
      } else if (status.includes('approve') || status === 'a' || status === 'd') {
        approved++;
      } else if (status.includes('reject') || status === 'r' || status === 'k' || status === 'x') {
        rejected++;
      } else {
        pending++;
      }
    });

    return { total: allItems.length, pending, approved, rejected, drafts };
  }, [allItems]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadRequests();
  };

  const handleClearFilters = () => {
    setFilters({
      query: '',
      requestStatus: '',
      server: '',
      module: '',
      plant: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const formatDate = (val?: string) => {
    if (!val) return '-';
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    } catch { }
    return val;
  };

  const handleDeleteDraft = async () => {
    const ref = deleteItem?.ReferenceNumber || deleteItem?.referenceNumber;
    if (!ref) return;
    setIsDeleting(true);
    try {
      const res = await dispatch(deleteDraftSapAuthRequest({ ReferenceNumber: String(ref), draft: true }));
      if (res.meta.requestStatus === 'fulfilled') {
        toast.success('Draft deleted successfully');
        setDeleteItem(null);
        loadRequests();
      } else {
        toast.error((res.payload as string) || 'Failed to delete draft');
      }
    } catch {
      toast.error('An error occurred while deleting draft');
    } finally {
      setIsDeleting(false);
    }
  };

  const isLoading = sapState.loading === 'pending';

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1 text-dark fw-bold">
              <i className="ti ti-lock-access me-2 text-danger"></i>SAP Authorization Forms
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.services.list}>Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  SAP Authorization Forms
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <button
              className={`btn btn-outline-secondary d-flex align-items-center ${showFilter ? 'active' : ''}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <i className={`ti ti-filter-${showFilter ? 'off' : 'check'} me-1`}></i>
              {showFilter ? 'Hide Filters' : 'Filters'}
            </button>
            <button
              className="btn btn-outline-primary d-flex align-items-center"
              onClick={handleSyncSapAuth}
              disabled={isSyncing}
              title="Sync SAP Authorization requests from SAP system"
            >
              <i className={`ti ti-arrows-rotate ${isSyncing ? 'ti-spin' : ''} me-1`}></i>
              {isSyncing ? 'Syncing...' : 'Sync SAP'}
            </button>
            <button
              className="btn btn-outline-secondary d-flex align-items-center"
              onClick={loadRequests}
              disabled={isLoading}
            >
              <i className={`ti ti-refresh ${isLoading ? 'ti-spin' : ''} me-1`}></i>
              Refresh
            </button>
            <Link to={routes.sapAuthorization.create} className="btn btn-danger d-flex align-items-center">
              <i className="ti ti-plus me-1"></i>New SAP Request
            </Link>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="row g-3 mb-4">
          <div className="col-xl-3 col-sm-6">
            <div className="card border-0 shadow-sm rounded-3 h-100 bg-white">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-12 fw-medium d-block mb-1">TOTAL REQUESTS</span>
                  <h3 className="mb-0 fw-bold text-dark">{kpis.total}</h3>
                </div>
                <div className="avatar avatar-md rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center">
                  <i className="ti ti-files fs-22"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6">
            <div className="card border-0 shadow-sm rounded-3 h-100 bg-white">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-12 fw-medium d-block mb-1">PENDING APPROVAL</span>
                  <h3 className="mb-0 fw-bold text-warning">{kpis.pending}</h3>
                </div>
                <div className="avatar avatar-md rounded-3 bg-warning-subtle text-warning d-flex align-items-center justify-content-center">
                  <i className="ti ti-clock fs-22"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6">
            <div className="card border-0 shadow-sm rounded-3 h-100 bg-white">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-12 fw-medium d-block mb-1">APPROVED</span>
                  <h3 className="mb-0 fw-bold text-success">{kpis.approved}</h3>
                </div>
                <div className="avatar avatar-md rounded-3 bg-success-subtle text-success d-flex align-items-center justify-content-center">
                  <i className="ti ti-circle-check fs-22"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6">
            <div className="card border-0 shadow-sm rounded-3 h-100 bg-white">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-12 fw-medium d-block mb-1">DRAFTS / REJECTED</span>
                  <h3 className="mb-0 fw-bold text-danger">
                    {kpis.drafts} <span className="text-muted fs-14 fw-normal">/ {kpis.rejected}</span>
                  </h3>
                </div>
                <div className="avatar avatar-md rounded-3 bg-danger-subtle text-danger d-flex align-items-center justify-content-center">
                  <i className="ti ti-file-alert fs-22"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilter && (
          <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
            <div className="card-header bg-light border-0 py-3 px-4 d-flex align-items-center justify-content-between">
              <h6 className="mb-0 fw-bold text-dark">
                <i className="ti ti-adjustments-horizontal me-2 text-danger"></i>Filter SAP Requests
              </h6>
              <button className="btn btn-sm btn-link text-muted p-0" onClick={handleClearFilters}>
                Clear All
              </button>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSearchSubmit}>
                <div className="row g-3">
                  <div className="col-md-4 col-sm-6">
                    <label className="form-label fs-13 fw-semibold text-muted mb-1">Search Keywords</label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white"><i className="ti ti-search text-muted"></i></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ref No, Emp ID, T-Code, Dept..."
                        value={filters.query}
                        onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="col-md-2 col-sm-6">
                    <label className="form-label fs-13 fw-semibold text-muted mb-1">Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={filters.requestStatus}
                      onChange={(e) => setFilters({ ...filters, requestStatus: e.target.value })}
                    >
                      <option value="">All Statuses</option>
                      <option value="A">Approved</option>
                      <option value="P">Pending / In-Process</option>
                      <option value="R">Rejected</option>
                      <option value="Draft">Draft</option>
                    </select>
                  </div>

                  <div className="col-md-2 col-sm-6">
                    <label className="form-label fs-13 fw-semibold text-muted mb-1">SAP Server</label>
                    <select
                      className="form-select form-select-sm"
                      value={filters.server}
                      onChange={(e) => setFilters({ ...filters, server: e.target.value })}
                    >
                      <option value="">All Servers</option>
                      {serversList.map((s) => (
                        <option key={s.Value} value={s.Value}>
                          {s.Ddtext} ({s.Value})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2 col-sm-6">
                    <label className="form-label fs-13 fw-semibold text-muted mb-1">SAP Module</label>
                    <select
                      className="form-select form-select-sm"
                      value={filters.module}
                      onChange={(e) => setFilters({ ...filters, module: e.target.value })}
                    >
                      <option value="">All Modules</option>
                      {modulesList.map((m) => (
                        <option key={m.Value} value={m.Value}>
                          {m.Value} - {m.Ddtext}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2 col-sm-6">
                    <label className="form-label fs-13 fw-semibold text-muted mb-1">Plant</label>
                    <select
                      className="form-select form-select-sm"
                      value={filters.plant}
                      onChange={(e) => setFilters({ ...filters, plant: e.target.value })}
                    >
                      <option value="">All Plants</option>
                      {plantsList.map((p) => (
                        <option key={p.Value} value={p.Value}>
                          {p.Value} - {p.Ddtext}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3 col-sm-6">
                    <label className="form-label fs-13 fw-semibold text-muted mb-1">Start Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={filters.startDate}
                      onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    />
                  </div>

                  <div className="col-md-3 col-sm-6">
                    <label className="form-label fs-13 fw-semibold text-muted mb-1">End Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={filters.endDate}
                      onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    />
                  </div>

                  <div className="col-md-6 col-sm-12 d-flex align-items-end gap-2">
                    <button type="submit" className="btn btn-sm btn-danger px-4">
                      <i className="ti ti-search me-1"></i>Apply Filters
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleClearFilters}>
                      Reset
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Requests Table Card */}
        <div className="card border-0 shadow-sm rounded-3 bg-white">
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="mb-0 fw-bold text-dark">
              <i className="ti ti-list me-2 text-danger"></i>SAP Authorization Requests
            </h5>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted fs-13">Showing {filteredItems.length} records</span>
            </div>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 text-nowrap">Reference Number</th>
                    <th className="text-nowrap">SAP Server</th>
                    <th className="text-nowrap">SAP module</th>
                    <th className="text-nowrap">Remarks</th>
                    <th className="text-nowrap">Request Creation Date</th>
                    <th className="text-nowrap">Request Status</th>
                    <th className="text-nowrap">Last/Latest Approver</th>
                    <th className="text-end pe-4 text-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-5">
                        <div className="spinner-border text-danger" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mt-2 mb-0 fs-13">Fetching SAP Authorization requests...</p>
                      </td>
                    </tr>
                  ) : filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-5">
                        <div className="avatar avatar-xl bg-light text-muted rounded-circle mb-3 mx-auto d-flex align-items-center justify-content-center">
                          <i className="ti ti-folder-off fs-32"></i>
                        </div>
                        <h6 className="fw-bold text-dark mb-1">No SAP Authorization Requests Found</h6>
                        <p className="text-muted fs-13 mb-3">
                          {filters.query || filters.requestStatus || filters.server || filters.module
                            ? 'No records match your active filter criteria. Try resetting filters.'
                            : 'You have not submitted any SAP Authorization requests yet.'}
                        </p>
                        <Link to={routes.sapAuthorization.create} className="btn btn-sm btn-danger">
                          <i className="ti ti-plus me-1"></i>Create New Request
                        </Link>
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item, idx) => {
                      const refNo = item.ReferenceNumber || item.referenceNumber || `REQ-${item.id || idx + 1}`;
                      const isDraft = Boolean(item.draft || item.is_draft);
                      const isNewUser = item.NewOrExistingUser === 'N';
                      const moduleLabel = getMasterText(item.SAPModule, modulesList) !== '-' ? getMasterText(item.SAPModule, modulesList) : (item.SAPModule || '-');
                      const serverLabel = getMasterText(item.SAPServer, serversList) !== '-' ? getMasterText(item.SAPServer, serversList) : (item.SAPServer || '-');
                      const plantName = getMasterText(item.Plant, plantsList);
                      const reqDate = item.requestCreationDate || item.createdAt || item.created_at;
                      const statusVal = item.RequestStatus || item.requestStatus || item.Status || item.status;

                      return (
                        <tr key={item.id || item.ReferenceNumber || idx}>
                          <td className="ps-4">
                            <div className="d-flex align-items-center gap-2">
                              <Link
                                to={`/sap-authorization/details/${refNo}`}
                                className="fw-bold text-primary text-decoration-none"
                              >
                                {refNo}
                              </Link>
                              {isDraft && (
                                <span className="badge bg-secondary-subtle text-secondary fs-10 px-1 py-0 border">
                                  DRAFT
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="text-dark fw-medium fs-13">
                            {serverLabel}
                          </td>

                          <td className="text-danger fw-semibold fs-13">
                            {moduleLabel}
                          </td>

                          <td className="text-muted fs-13" style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.Remarks || item.remarks || ''}>
                            {item.Remarks || item.remarks || '-'}
                          </td>

                          <td className="text-nowrap fs-13 text-muted">{formatDate(reqDate)}</td>

                          <td>{getSapStatusBadge(statusVal, isDraft)}</td>

                          <td className="text-nowrap fs-13">
                            <span className="text-dark fw-medium d-block">
                              {item.ApproverName || item.approverName || item.lastApprover || item.LastApprover || '-'}
                            </span>
                            {(item.ApproverUserID || item.approverUserID || item.ApproverID || item.approverID) && (
                              <small className="text-muted fs-11">
                                ID: {item.ApproverUserID || item.approverUserID || item.ApproverID || item.approverID}
                              </small>
                            )}
                          </td>

                          <td className="text-end pe-4">
                            <div className="d-flex align-items-center justify-content-end gap-1">
                              <Link
                                to={`/sap-authorization/details/${refNo}`}
                                className="btn btn-xs btn-outline-primary px-2 py-1"
                                title="View Details"
                              >
                                <i className="ti ti-eye"></i>
                              </Link>
                              {isDraft && (
                                <>
                                  <Link
                                    to={`/sap-authorization/edit/${refNo}`}
                                    className="btn btn-xs btn-outline-warning px-2 py-1"
                                    title="Edit Draft"
                                  >
                                    <i className="ti ti-edit"></i>
                                  </Link>
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-outline-danger px-2 py-1"
                                    title="Delete Draft"
                                    onClick={() => setDeleteItem(item)}
                                  >
                                    <i className="ti ti-trash"></i>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="card-footer bg-white border-top py-3 px-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted fs-13">Page size:</span>
                <select
                  className="form-select form-select-sm w-auto"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1 || isLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <i className="ti ti-chevron-left me-1"></i>Previous
                </button>
                <span className="btn btn-sm btn-light border px-3 text-dark fw-bold fs-13">Page {page}</span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={filteredItems.length < limit || isLoading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next<i className="ti ti-chevron-right ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Draft Modal */}
        {deleteItem && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header border-0 pb-0">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setDeleteItem(null)}
                    disabled={isDeleting}
                  ></button>
                </div>
                <div className="modal-body text-center px-4 pb-4">
                  <div className="avatar avatar-xl bg-danger-subtle text-danger rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                    <i className="ti ti-alert-triangle fs-36"></i>
                  </div>
                  <h5 className="fw-bold text-dark mb-2">Delete Draft Request?</h5>
                  <p className="text-muted fs-13 mb-4">
                    Are you sure you want to delete draft SAP request{' '}
                    <strong className="text-dark">
                      {deleteItem.ReferenceNumber || deleteItem.referenceNumber || 'this request'}
                    </strong>
                    ? This action cannot be undone.
                  </p>
                  <div className="d-flex justify-content-center gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4"
                      onClick={() => setDeleteItem(null)}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger px-4"
                      onClick={handleDeleteDraft}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          Deleting...
                        </>
                      ) : (
                        'Yes, Delete Draft'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SapAuthorizationList;
